import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { getNotificationPreferences, updateNotificationPreferences } from '@/lib/rpc/user/notifications';

export interface NotificationPreferences {
  id?: string;
  customer_id: string;
  user_id?: string; // Keep for backwards compatibility
  email_enabled: boolean;
  in_app_enabled: boolean;
  push_enabled: boolean;
  
  // User notification categories
  kyc_updates: boolean;
  transaction_updates: boolean;
  security_alerts: boolean;
  account_updates: boolean;
  maintenance_notices: boolean;
  marketing: boolean;
  
  // Admin notification categories
  system_alerts: boolean;
  business_reports: boolean;
  operations_alerts: boolean;
  
  // Delivery preferences
  digest_frequency: 'immediate' | 'hourly' | 'daily' | 'weekly';
  quiet_hours_start: string;
  quiet_hours_end: string;
  timezone: string;
}

const defaultPreferences: Partial<NotificationPreferences> = {
  email_enabled: true,
  in_app_enabled: true,
  push_enabled: false,
  kyc_updates: true,
  transaction_updates: true,
  security_alerts: true,
  account_updates: true,
  maintenance_notices: true,
  marketing: false,
  system_alerts: false,
  business_reports: false,
  operations_alerts: false,
  digest_frequency: 'immediate',
  quiet_hours_start: '22:00',
  quiet_hours_end: '08:00',
  timezone: 'UTC',
};

export function useNotificationPreferences(customerId: string) {
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (customerId) {
      loadPreferences();
    }
  }, [customerId]);

  const loadPreferences = async () => {
    try {
      const result = await getNotificationPreferences();
      
      if (!result.ok) {
        console.error('Error loading notification preferences:', result);
        return;
      }

      if (result.data) {
        setPreferences(result.data as unknown as NotificationPreferences);
      } else {
        // Create default preferences for new customer
        const newPreferences = {
          customer_id: customerId,
          user_id: null,
          ...defaultPreferences,
        } as Partial<NotificationPreferences>;
        
        const createResult = await updateNotificationPreferences(newPreferences);
        
        if (!createResult.ok) {
          console.error('Error creating notification preferences:', createResult);
          return;
        }

        // Reload after creation
        await loadPreferences();
      }
    } catch (error) {
      console.error('Error in loadPreferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updatePreferences = async (updates: Partial<NotificationPreferences>) => {
    if (!preferences) return;

    setIsSaving(true);
    const previousPreferences = preferences;
    const optimisticPreferences = { ...previousPreferences, ...updates };
    setPreferences(optimisticPreferences);

    try {
      const result = await updateNotificationPreferences(updates);

      if (!result.ok) {
        console.error('Error updating notification preferences:', result);
        setPreferences(previousPreferences);
        toast({
          title: 'Error',
          description: 'Failed to update notification preferences',
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Settings saved',
        description: 'Your notification preferences have been updated',
      });
    } catch (error) {
      console.error('Error in updatePreferences:', error);
      setPreferences(previousPreferences);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    preferences,
    isLoading,
    isSaving,
    updatePreferences,
    refreshPreferences: loadPreferences,
  };
}